<template>
	<v-row dense class="ma-0">
		<v-data-table
			:loading="loading"
			:headers="headers"
			:no-data-text="$t('no-data-available')"
			:loading-text="$t('loading')"
			:search="appBarSearch"
			:items="filteredUsers"
			:footer-props="getDataTableFooterProps('patients')"
			:items-per-page="-1"
			sort-by="id"
			item-key="id"
			fixed-header
			style="width: 100%"
			dense
			:height="pageHeight(174)"
			class="medical-staff-data-table">
			<template v-slot:item="{ item, index }">
				<tr :class="getRowsForDataTable(index)" @click="userDetails(item)">
					<td class="table-font-size px-0">
						<v-chip class="ml-2 d-flex align-center justify-center" x-small :color="getColorStatus(item.status)">
							{{ getStatus(item.status) }}
						</v-chip>
					</td>
					<td class="pl-2">
						<v-row dense class="d-flex align-center py-1">
							<v-col class="pl-2">
								<v-row dense>
									<span class="px-2 table-font-size">{{ item.firstName }} {{ item.lastName }}</span>
								</v-row>
							</v-col>
						</v-row>
					</td>
					<td class="table-font-size px-0">
						<v-chip label pill x-small color="primary">{{ calculateAge(item.birthdate) }} </v-chip>
					</td>
					<td class="table-font-size px-0">
						{{ formatTimestamp(item.birthdate) }}
					</td>
					<td class="table-font-size px-0">
						<span>{{ item.gender === 'M' ? $t('male') : $t('female') }}</span>
					</td>
					<td class="px-0 d-flex align-center justify-center">
						<v-tooltip
							v-for="device in allDevices"
							:key="device.deviceId"
							top
							:color="isDeviceAssigned(item, device.deviceId) ? 'primary' : 'grey'">
							<template v-slot:activator="{ on, attrs }">
								<img
									class="mx-1"
									:src="require('@/assets/icons/' + getDeviceIcon(item, device.deviceId))"
									:width="$vuetify.breakpoint.mdAndDown ? '15px' : '18'"
									:height="$vuetify.breakpoint.mdAndDown ? '15px' : '18'"
									v-bind="attrs"
									v-on="on" />
							</template>
							<span>{{ device.deviceName }}</span>
						</v-tooltip>
					</td>
					<td
						v-if="
							(item.isRpmEnabled || item.isRtmEnabled || item.isCcmEnabled) &&
							item.isLicensed &&
							item.status === 1 &&
							false
						">
						<v-btn
							@click.stop="openDeassignDialog(item)"
							min-width="12"
							max-width="12"
							color="primary"
							class="white--text"
							x-small>
							{{ $t('deassign-license') }}
						</v-btn>
					</td>

					<td
						v-else-if="
							(item.isRpmEnabled || item.isRtmEnabled || item.isCcmEnabled) &&
							!item.isLicensed &&
							item.status === 1 &&
							false
						">
						<v-btn
							@click.stop="syncronizeDrawers(item)"
							min-width="12"
							max-width="12"
							color="green"
							class="white--text"
							x-small>
							{{ $t('assign-licence') }}
						</v-btn>
					</td>

					<!-- In all other cases, render an empty cell -->
					<td v-else></td>
					<td class="text-left">
						<v-row dense class="d-flex align-center py-1">
							<v-col class="text-left" cols="10">
								<v-row dense :class="item.status === 99 ? 'disabled-row' : ''">
									<v-tooltip top :color="item.isRpmEnabled ? 'primary' : ''">
										<template v-slot:activator="{ on }">
											<span
												v-on="on"
												class="font-weight-medium"
												@click.stop="openStatusDialog(item.id, 'RPM', !item.isRpmEnabled)">
												<i v-if="item.isRpmEnabled" class="mdi mdi-checkbox-blank mdi-18px color-primary"></i>
												<i v-else class="mdi mdi-checkbox-blank-outline mdi-18px color-red color-primary"></i>
											</span>
										</template>
										<span>{{ item.isRpmEnabled ? 'RPM Enrolled' : 'Not RPM Enrolled' }}</span>
									</v-tooltip>
								</v-row>
							</v-col>
						</v-row>
					</td>
					<td>
						<v-row dense class="d-flex align-center py-1">
							<v-col class="text-left" cols="10">
								<v-row dense :class="item.status === 99 ? 'disabled-row' : ''">
									<v-tooltip top :color="item.isRtmEnabled ? 'primary' : ''">
										<template v-slot:activator="{ on }">
											<span
												v-on="on"
												class="font-weight-medium"
												@click.stop="openStatusDialog(item.id, 'RTM', !item.isRtmEnabled)">
												<i v-if="item.isRtmEnabled" class="mdi mdi-checkbox-blank mdi-18px color-primary"></i>
												<i v-else class="mdi mdi-checkbox-blank-outline mdi-18px color-red color-primary"></i>
											</span>
										</template>
										<span>{{ item.isRtmEnabled ? 'RTM Enrolled' : 'Not RTM Enrolled' }}</span>
									</v-tooltip>
								</v-row>
							</v-col>
						</v-row>
					</td>
					<td>
						<v-row dense class="d-flex align-center py-1">
							<v-col class="text-left" cols="10">
								<v-row dense :class="item.status === 99 ? 'disabled-row' : ''">
									<v-tooltip top :color="item.isCcmEnabled ? 'primary' : ''">
										<template v-slot:activator="{ on }">
											<span
												v-on="on"
												class="font-weight-medium"
												@click.stop="openStatusDialog(item.id, 'CCM', !item.isCcmEnabled)">
												<i v-if="item.isCcmEnabled" class="mdi mdi-checkbox-blank mdi-18px color-primary"></i>
												<i v-else class="mdi mdi-checkbox-blank-outline mdi-18px color-red color-primary"></i>
											</span>
										</template>
										<span>{{ item.isCcmEnabled ? 'CCM Enrolled' : 'Not CCM Enrolled' }}</span>
									</v-tooltip>
								</v-row>
							</v-col>
						</v-row>
					</td>
					<td>
						<v-row v-if="item.status !== 99" dense class="d-flex align-center py-1">
							<v-col class="text-left" cols="10">
								<v-row dense>
									<span class="pl-4 font-weight-medium">
										<i
											v-if="item.patientHasDoneReadingForToday"
											class="mdi mdi-checkbox-blank mdi-18px color-primary"></i>
										<i v-else class="mdi mdi-checkbox-blank-outline mdi-18px color-red color-primary"></i>
									</span>
								</v-row>
							</v-col>
						</v-row>
					</td>
					<td class="px-2">
						<v-switch
							v-if="item.status !== -1 && item.status !== 99 && item.status !== 10"
							density="compact"
							readonly
							@click.stop="setUserStatus(item)"
							v-model="item.status"
							class="mt-0 pt-0"
							hide-details />
					</td>
					<td v-if="roleName === 'HOSPITAL_ADMIN' || roleName === 'DOCTOR' || roleName === 'NURSE'" class="pr-0">
						<div v-if="item.status !== 0 && item.status !== 99" :disabled="!item.hasMedicalTeam">
							<div v-if="item.status !== 1" class="text-left">
								<v-tooltip color="primary" left>
									<template v-slot:activator="{ on }">
										<v-icon
											size="20"
											v-on="on"
											@click.stop="sendActivationEmail(item, 'USER_CONFIRMATION')"
											color="primary">
											mdi-email
										</v-icon>
									</template>
									<span>{{ $t('validateEmail') }}</span>
								</v-tooltip>
							</div>
							<div v-else>
								<v-tooltip left color="primary">
									<template v-slot:activator="{ on }">
										<v-icon
											class="float-left"
											@click.stop="sendActivationEmail(item, 'RESET_PASSWORD')"
											color="primary"
											size="23"
											v-on="on">
											mdi-lock-reset
										</v-icon>
									</template>
									<span>{{ $t('reset-password') }}</span>
								</v-tooltip>
							</div>
						</div>
					</td>
				</tr>
			</template>
		</v-data-table>
		<v-dialog
			v-if="showChangeStatusDialog"
			v-model="showChangeStatusDialog"
			max-width="400px"
			transition="dialog-top-transition"
			persistent>
			<v-card>
				<v-card-title class="mx-auto primary2 py-1 font-size16 text-color-white">
					{{ $t('change') }} {{ $t('status') }}
				</v-card-title>
				<v-card-text class="py-5">
					{{ $t('change-status-to-new-value', { statusName: changeStatusName, newValue: changingStatusTo }) }}
				</v-card-text>
				<v-card-actions class="d-flex justify-center">
					<v-btn @click="changeStatus()" color="primary2" class="text-color-white">{{ $t('yes') }}</v-btn>
					<v-btn @click="showChangeStatusDialog = false">{{ $t('no') }}</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<confirm-status-dialog
			:dialog="confirmDialog"
			@confirmToggleStatus="confirmToggleStatus"
			@update:dialog="confirmDialog = $event"
			v-if="confirmDialog" />
		<DeAssignLicenseConfirmationDialog
			:patientDialog="patientDialog"
			:licenseId="licenseId"
			:patientName="patientName"
			@update:patientDialog="patientDialog = $event"
			v-if="patientDialog" />
	</v-row>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { mailPurple } from '@/assets';
import {
	showSuccessAlert,
	showErrorAlert,
	getDataTableFooterProps,
	calculateAge,
	getRowsForDataTable,
	formatTimestamp,
} from '@/utils/utilities';
import ConfirmStatusDialog from '@/views/dialogsNew/ConfirmStatusDialog.vue';
import DeAssignLicenseConfirmationDialog from '@/views/dialogsNew/DeAssignLicenseConfirmationDialog.vue';

export default {
	components: { ConfirmStatusDialog, DeAssignLicenseConfirmationDialog },
	data () {
		return {
			patientDialog: false,
			licenseId: null,
			patientName: null,
			allDevices: [
				{ deviceId: 1, deviceName: 'Thermometer' },
				{ deviceId: 2, deviceName: 'Oximeter' },
				{ deviceId: 3, deviceName: 'Blood Pressure' },
				{ deviceId: 6, deviceName: 'Glucose Meter' },
				{ deviceId: 5, deviceName: 'Weight Scale' },
			],
			confirmDialog: false,
			userToToggle: null,
			loading: true,
			editEmailDialog: false,
			dialog: false,
			patientEmailChange: '',
			mailPurple: mailPurple,
			showChangeStatusDialog: false,
			changeStatusName: '',
			changingStatusUserId: null,
			changingStatusTo: false,
		};
	},
	computed: {
		...mapState({
			userData: (state) => state.authentication.userData,
			relatedPatients: (state) => state.hospitals.relatedPatients,
			verifiersFromRepo: (state) => state.verifiers.verifiers,
		}),
		...mapGetters({
			roleName: 'authentication/getRole',
			pageHeight: 'pageHeight',
			appBarSearch: 'filterbar/getSearch',
			appBarUserStatusVerifier: 'filterbar/getUserStatusVerifier',
			appBarBillingTypeFilterVerifier: 'filterbar/getRpmValue',
			appBarHospitalFilterVerifier: 'filterbar/getHospitalsValue',
		}),
		headers () {
			const headers = [
				{
					text: this.$t('status'),
					value: 'status',
					class: 'table-header',
					align: 'start pl-3',
					width: 50,
				},
				{
					text: this.$t('name'),
					value: 'fullName',
					class: 'table-header',
					align: 'start pl-3',
					width: 250,
				},
				{
					text: this.$t('header-age'),
					value: '',
					class: 'table-header pl-1',
					width: 50,
					sortable: false,
				},
				{
					text: this.$t('birthdate'),
					value: '',
					class: 'table-header pl-1',
					width: 100,
					sortable: false,
				},
				{
					text: this.$t('header-sex'),
					value: 'gender',
					class: 'table-header',
					width: 50,
				},
				{
					text: this.$t('devices'),
					value: '',
					class: 'table-header pl-3 px-0',
					align: 'center',
					sortable: false,
				},
				{
					text: '', // this.$t('assign-licence'),
					value: '',
					class: 'table-header pl-3 px-0',
					align: 'center',
					sortable: false,
				},
				{
					text: 'RPM',
					value: 'isRpmEnabled',
					class: 'table-header px-0 pl-2 mx-0',
					width: 50,
					sortable: false,
				},
				{
					text: 'RTM',
					value: 'isRtmEnabled',
					class: 'table-header px-0 pl-2 mx-0',
					width: 50,
					sortable: false,
				},
				{
					text: 'CCM',
					value: 'isCcmEnabled',
					class: 'table-header px-0 pl-2 mx-0',
					width: 50,
					sortable: false,
				},
				{
					text: this.$t('header-reading'),
					value: 'reading',
					class: 'table-header px-0 mx-0',
					width: 50,
					sortable: false,
				},
				{
					text: this.$t('set-status'),
					value: 'status',
					class: 'table-header px-2',
					sortable: false,
					width: 50,
				},
				{
					text: this.$t('actions'),
					value: 'emailReset',
					class: 'table-header',
					sortable: false,
					align: 'start pl-2',
					width: 50,
				},
			];
			return headers;
		},
		filteredUsers () {
			this.loading = true;
			if (this.relatedPatients) {
				this.relatedPatients.forEach((patient) => {
					patient.fullName = `${patient.firstName} ${patient.lastName}`;
				});
			}

			let calculateFilteredPatients = this.relatedPatients || [];

			// Filter based on status
			// ALL === -99
			// PENDING === -1
			// ACTIVE === 1
			// DECEASED === 99
			// DELETED === 10
			// INACTIVE === 0

			if (
				this.appBarUserStatusVerifier === 10 ||
				this.appBarUserStatusVerifier === 99 ||
				this.appBarUserStatusVerifier === 1 ||
				this.appBarUserStatusVerifier === -1 ||
				this.appBarUserStatusVerifier === 0
			) {
				calculateFilteredPatients = calculateFilteredPatients.filter((u) => u.status === this.appBarUserStatusVerifier);
			}
			// Filter based on RPM status
			if (this.userData.isSequoiaUser === false || this.userData.isSequoiaUser === null) {
				this.loading = false;
				return calculateFilteredPatients;
			}
			if (this.appBarBillingTypeFilterVerifier === 1) {
				this.loading = false;
				return calculateFilteredPatients;
			}
			if (this.appBarBillingTypeFilterVerifier === 0) {
				calculateFilteredPatients = calculateFilteredPatients.filter(
					(u) => u.isRpmEnabled || u.isRtmEnabled || u.isCcmEnabled,
				);
			}
			if (this.appBarHospitalFilterVerifier.length > 0) {
				calculateFilteredPatients = calculateFilteredPatients.filter(
					(u) =>
						this.appBarHospitalFilterVerifier.includes(0) ||
						this.appBarHospitalFilterVerifier.includes(u.assignedToHospitalId),
				);
			}
			this.loading = false;
			return calculateFilteredPatients;
		},
	},
	async mounted () {
		await this.getPatients();
	},
	methods: {
		formatTimestamp,
		calculateAge,
		getRowsForDataTable,
		getDataTableFooterProps,
		openDeassignDialog (item) {
			this.patientDialog = true;
			this.licenseId = item.licenseAssignmentId;
			this.patientName = item.fullName;
		},
		openStatusDialog (itemId, changeName, newValue) {
			this.changingStatusUserId = itemId;
			this.changeStatusName = changeName;
			this.changingStatusTo = newValue;
			this.showChangeStatusDialog = true;
		},
		async changeStatus () {
			this.showChangeStatusDialog = false;
			this.loading = true;
			if (this.changeStatusName === 'RPM') {
				await this.$store.dispatch('hospitals/setRPMStatus', {
					userId: this.changingStatusUserId,
					valueToSet: this.changingStatusTo,
				});
			} else if (this.changeStatusName === 'RTM') {
				await this.$store.dispatch('hospitals/setRTMStatus', {
					userId: this.changingStatusUserId,
					valueToSet: this.changingStatusTo,
				});
			} else {
				await this.$store.dispatch('hospitals/setCCMStatus', {
					userId: this.changingStatusUserId,
					valueToSet: this.changingStatusTo,
				});
			}
			this.loading = false;
		},
		async confirmToggleStatus () {
			const requestData = {
				userId: this.userToToggle.id,
				statusValueToSet: this.userToToggle.status ? 0 : 1,
			};
			this.$store.dispatch('users/setUserStatus', requestData).then(() => {
				this.confirmDialog = false;
				this.getPatients();
			});
		},
		syncronizeDrawers (item) {
			var parameters = {};
			parameters = {
				detailsDrawer: true,
				page: 'patientLicence',
				item: item,
			};
			this.$store.commit('SET_DETAILS_DRAWER', parameters);
		},
		getColorStatus (status) {
			switch (status) {
				case 1:
					return 'background-green-dot-status';
				case 0:
					return 'grey';
				case 10:
					return 'background-red-dot-status';
				case 99:
					return 'background-black-dot-status';
			}
		},
		getStatus (status) {
			switch (status) {
				case 1:
					return this.$t('active');
				case 0:
					return this.$t('inactive');
				case -1:
					return this.$t('pending');
				case 10:
					return this.$t('deleted');
				case 99:
					return this.$t('deceased');
			}
		},
		getDeviceIcon (item, deviceId) {
			const iconMap = {
				1: { primary: 'thermometer_primary.svg', grey: 'thermometer_grey.svg' },
				2: { primary: 'oxymeter_primary.svg', grey: 'oxymeter_grey.svg' },
				3: {
					primary: 'blood_pressure_primary.svg',
					grey: 'blood_pressure_grey.svg',
				},
				5: { primary: 'scale_primary.svg', grey: 'scale_grey.svg' },
				6: {
					primary: 'glucose_meter_primary.svg',
					grey: 'glucose_meter_grey.svg',
				},
			};

			const treatmentPlanDevicesList = item?.patientActiveDevicesFromTreatmentPlan?.treatmentPlanDevicesList;

			if (!treatmentPlanDevicesList) {
				return iconMap[deviceId]?.grey;
			}

			const device = treatmentPlanDevicesList.find((device) => device.deviceType === deviceId);
			return device ? iconMap[deviceId]?.primary : iconMap[deviceId]?.grey;
		},
		isDeviceAssigned (item, deviceId) {
			const treatmentPlanDevicesList = item?.patientActiveDevicesFromTreatmentPlan?.treatmentPlanDevicesList;

			if (!treatmentPlanDevicesList) {
				return false;
			}

			return treatmentPlanDevicesList.some((device) => device.deviceType === deviceId);
		},
		async setUserStatus (item) {
			this.userToToggle = item;
			this.confirmDialog = true;
		},
		async userDetails (item) {
			item.medTeam = item.hasMedicalTeam === true ? 1 : 0;
			// Do not remove this , checks if the medical team exists when patient is selected.
			if (!item.medTeam) {
				showErrorAlert(this.$t('medicalTeamCheck'));
			} else {
				await this.$store
					.dispatch('users/getUserById', item.id)
					.then(() => {
						return this.$router.push({
							name: 'Patient Details',
							params: { id: `${item.id}` },
						});
					})
					.then(() => {
						return this.$store.dispatch('filterbar/resetSearch');
					});
			}
		},
		// Methods are connected to the v-dialog , which is not in use . line 6
		// closeEditEmailDialog () {
		//   this.editEmailDialog = false;
		// },
		// editInvalidEmail () {
		//   const body = {
		//     userId: this.patientEmailChange.id,
		//     newEmailValue: this.patientEmailChange.email,
		//   };
		//   this.$store.dispatch('users/editInvalidEmail', body).then(async (res) => {
		//     if (res.data.resFlag) {
		//       showSuccessAlert(this.$t('success'));
		//       this.editEmailDialog = false;
		//     } else {
		//       showErrorAlert(this.$t('failed!'));
		//     }
		//   });
		// },
		async getPatients () {
			this.loading = true;
			await this.$store.dispatch('hospitals/getRelatedPatientsV4', this.userData.hospitalAssignedData.id).then(() => {
				this.loading = false;
			});
		},
		// closeDialog () {
		//   this.dialog = false;
		//   this.getPatients();
		// },
		// success (res) {
		//   this.dialog = false;
		//   this.getPatients();
		//   if (res.resFlag) {
		//     showSuccessAlert(this.$t('success'));
		//   } else {
		//     showSuccessAlert(this.$t('failed!'));
		//   }
		// },
		async sendActivationEmail (item, validationType) {
			if (validationType === null || validationType === undefined) {
				validationType = 'RESET_PASSWORD';
			}
			const sendValidationEmail = {
				email: item.email,
				validationType: validationType,
			};
			await this.$store.dispatch('users/sendValidationLinkByEmail', sendValidationEmail).then((res) => {
				if (res.resFlag) {
					showSuccessAlert(this.$t('success'));
				} else {
					showSuccessAlert(this.$t('failed!'));
				}
			});
		},
	},
};
</script>
